import { LabelType, Options } from '@angular-slider/ngx-slider';
import { padStart } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnCamera, SnNotification, SnNotificationGroupType, SnUser } from '@lib/api/entities';
import {
  SnCamerasAPI,
  SnNotificationGroupsAPI,
  SnSavedSearchAPI,
  SnUsersAPI,
} from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation/helpers';
import { SnDrawerService } from '@lib/ui/drawer';

import { SentryScheduleDialogComponent } from '../sentry-schedule-dialog/sentry-schedule-dialog.component';
import { SentrySearchResultsDrawerComponent } from '../sentry-search-results-drawer';

@Component({
  selector: 'fs-create-sentry-dialog',
  templateUrl: './create-sentry-dialog.component.html',
  styleUrls: ['./create-sentry-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateSentryDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  get params$(): Observable<any> {
    return this._params$.asObservable();
  }
  get colors$(): Observable<any[]> {
    return this._colors$.asObservable();
  }
  get vehicleTypes$(): Observable<any[]> {
    return this._vehicleTypes$.asObservable();
  }
  get detectionLabels$(): Observable<any[]> {
    return this._detectionLabels$.asObservable();
  }
  get eventLabels$(): Observable<any[]> {
    return this._eventLabels$.asObservable();
  }
  get eventRules$(): Observable<any[]> {
    return this._eventRules$.asObservable();
  }
  get colorTop$(): Observable<any[]> {
    return this._colorTop$.asObservable();
  }
  get colorBottom$(): Observable<any[]> {
    return this._colorBottom$.asObservable();
  }

  public readonly manualRefresh$ = new Subject<void>();

  public readonly isNew: boolean;
  public timezone: string;
  public schedules: string[] = ['* * * * *'];
  public returnData: any = {};

  @ViewChild('timeSlider', { static: true })
  public timeSlider: any;

  public cameras = [];
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public notificationForm: UntypedFormGroup;
  public formModel: {
    name?: any;
    aiCameras?: any;
    eventDetection?: any;
    label?: any;
    eventLabel?: any;
    eventRule?: any;
    eventConfidence?: any;
    colorTop?: any;
    colorBottom?: any;
    vehicleType?: any;
    carColors?: any;
    confidence?: any;
    colorConfidence?: any;
    is_male?: string;
    has_bag?: string;
    has_backpack?: string;
    has_hat?: string;
    startDate?: Date;
    noEnd?: any;
    endDate?: Date;
    isActiveAlert?: any;
    notificationCreateAlarm?: boolean;
    lockTime?: number;
  } = {};
  public startTime = 0;
  public endTime = 1440;
  public createOrEdit: string;
  public isLoading: boolean = false;
  public createSentryMode: boolean = true;
  public notificationArray: UntypedFormArray;
  public selectedUsers: SnUser[] = [];
  public types: SnNotificationGroupType[] = [];
  public isBuilding: boolean = false;
  public loadingFacets: boolean = false;

  public sliderOptions: Options = {
    floor: 0,
    ceil: 1440,
    step: 60,
    translate: (value: number, label: LabelType): string => {
      const num = value;
      const hours = num / 60;
      const rhours = Math.floor(hours);
      const minutes = (hours - rhours) * 60;
      const rminutes = Math.round(minutes);
      return `${padStart(rhours.toString(), 2, '0')}:${padStart(rminutes.toString(), 2, '0')}`;
    },
  };
  public formFields: SnFormFieldConfig[];
  public readonly notificationTypes: string[];

  private readonly _params$ = new Subject<any>();
  private readonly _colors$ = new Subject<any[]>();
  private readonly _vehicleTypes$ = new Subject<any[]>();
  private readonly _detectionLabels$ = new Subject<any[]>();
  private readonly _eventLabels$ = new Subject<any[]>();
  private readonly _eventRules$ = new Subject<any[]>();
  private readonly _colorTop$ = new Subject<any[]>();
  private readonly _colorBottom$ = new Subject<any[]>();
  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly _dialogRef: MatDialogRef<CreateSentryDialogComponent>,
    private readonly _notificationGroupsAPI: SnNotificationGroupsAPI,
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _savedSearchAPI: SnSavedSearchAPI,
    private readonly _toastrService: ToastrService,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _usersAPI: SnUsersAPI,
    private readonly _matDialog: MatDialog,
    private readonly _cd: ChangeDetectorRef,
    private readonly _drawer: SnDrawerService,
  ) {
    this._initForm();

    this._notificationGroupsAPI
      .getTypeNotificationGroup()
      .pipe(takeUntil(this._destroy$))
      .subscribe((types) => (this.types = types.results));
  }

  public ngOnInit(): void {
    if (this.data) {
      if (this.data.id) {
        this.createSentryMode = false;
        this.createOrEdit = t('map.tools.createSentryDialog.editSentry');
        this._patchEditForm(this.data);
        if (this.data.rule.id) {
          this.timezone = this.data.rule.timezone;
        }
      } else {
        this.createOrEdit = t('map.snapAiSelector.createSentry');
        this._patchForm(this.data);
        this.startTime = this.data.timeSliderStart || this.startTime;
        this.endTime = this.data.timeSliderEnd || this.endTime;
      }
      this._setForm();
    }
    this._fetchFacets();
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.manualRefresh$.next();
    }, 750);
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public setSchedule(): void {
    const dialog = this._matDialog.open(SentryScheduleDialogComponent, {
      data: {
        timezone: this.timezone,
        schedules: this.schedules,
      },
    });

    dialog
      .afterClosed()
      .pipe(
        filter((data) => !!data?.schedules),
        takeUntil(this._destroy$),
      )
      .subscribe(({ timezone, schedules }) => {
        this.timezone = timezone;
        this.schedules = schedules;
      });
  }

  public submitForm(): void {
    this.isLoading = true;
    const formData = this.form.value;
    const mode = this.formModel.eventDetection;
    const queryParams = this._formToParams(formData, mode);
    
    this._savedSearchAPI
      .saveSentrySearch({
        name: formData.name,
        queryParams,
        isActiveAlert: true,
        notificationCreateAlarm: formData.notificationCreateAlarm,
        lockTime: formData.lockTime || 1800,
      })
      .pipe(
        switchMap((search: any) => {
          this.returnData = {
            name: formData.name,
            searchId: search.id,
            queryParams,
            notificationUsers: formData.notificationUsers,
            isActiveAlert: search.isActiveAlert,
            schedules: this.schedules,
            timezone: this.timezone,
          };
          return this._savedSearchAPI.setSearchRules(search.id, {
            notificationUsers: formData.notificationUsers,
            isActiveAlert: search.isActiveAlert,
            schedules: this.schedules,
            timezone: this.timezone,
            lockTime: formData.lockTime || 1800,
          });
        }),
        takeUntil(this._destroy$),
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          this._toastrService.success(t('map.tools.createSentryDialog.sentryCreatedSuccessfully'));
          this._dialogRef.close({ data: this.returnData });
        },
        error: () => {
          this.isLoading = false;
          this._toastrService.error(t('map.tools.createSentryDialog.unableToCreateSentry'));
        },
      });
  }
  public cancel(): void {
    this._dialogRef.close();
  }

  public updateSentrySearch(): void {
    this.isLoading = true;
    const formData = this.form.value;
    const mode = this.formModel.eventDetection;
    const queryParams = this._formToParams(formData, mode);
    let updatedSearch;
    this._savedSearchAPI
      .updateSearch(this.data.id, {
        name: formData.name,
        queryParams,
        isActiveAlert: true,
        notificationCreateAlarm: formData.notificationCreateAlarm,
        lockTime: formData.lockTime,
      })
      .pipe(
        switchMap((search: any) => {
          updatedSearch = search;
          return this._savedSearchAPI.setSearchRules(search.id, {
            notificationUsers: formData.notificationUsers,
            isActiveAlert: search.isActiveAlert,
            schedules: this.schedules,
            timezone: this.timezone,
          });
        }),
        takeUntil(this._destroy$),
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          this._toastrService.success(t('map.tools.createSentryDialog.sentryUpdatedSuccessfully'));
          this._dialogRef.close(updatedSearch);
        },
        error: () => {
          this.isLoading = false;
          this._toastrService.error(t('map.tools.createSentryDialog.unableToUpdateSentry'));
          this._dialogRef.close(updatedSearch);
        },
      });
    this.createSentryMode = false;
  }

  public createMode(): boolean {
    return this.createSentryMode;
  }

  public addNotification(): void {
    this._setNotification(new SnNotification());
  }

  public onNotificationDeleted(index: number): void {
    this.notificationArray.removeAt(index);
  }

  public searchThisSentry(): void {
    const formData = this.form.value;
    const mode = this.formModel.eventDetection;
    const params = this._formToParams(formData, mode);
    this._drawer.open(SentrySearchResultsDrawerComponent, {
      data: params,
      mode: 'side',
      width: 380,
      closeOnNavigation: true,
      panelClass: ['hide-on-fullscreen'],
    });
    this._dialogRef.close();
  }

  private _initForm(): void {
    this.notificationArray = this._formBuilder.array([]);

    this.form = this._formBuilder.group({
      notificationUsers: this.notificationArray,
    });
  }

  private _setNotification(notification: any): void {
    this.notificationArray.push(
      this._formBuilder.group({
        id: notification.id,
        notificationType: [notification.notificationType, Validators.required],
        user: [notification.user, Validators.required],
        email: notification.email,
        mobilePhone: notification.mobilePhone,
      }),
    );
  }

  private _formToParams(formData: any, mode: string): any {
    const cameras = formData.aiCameras;
    const date = new Date();
    const offset = (date.getTimezoneOffset() * -1) / 60;
    const startTime = `${this.timeSlider.value / 60}, ${offset}`;
    const endTime = `${this.timeSlider.highValue / 60}, ${offset}`;
    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    const eventRule = formData.eventRule;
    let params: any;

    if (mode === 'detection') {
      params = {
        camera_id__in: cameras.join(','),
        label__in: formData.label,
        timestamp__gte: Math.floor(startDate.getTime() / 1000),
        timestamp__lte: formData.noEnd ? null : Math.ceil(endDate.getTime() / 1000),
        timestamp__hour__gte__offset: startTime,
        timestamp__hour__lt__offset: endTime,
        confidence__gte: formData.confidence,
      };
    }

    if (mode === 'event') {
      params = {
        camera_id__in: cameras.join(','),
        events__label__in: formData.eventLabel,
        timestamp__gte: Math.floor(startDate.getTime() / 1000),
        timestamp__lte: formData.noEnd ? null : Math.ceil(endDate.getTime() / 1000),
        timestamp__hour__gte__offset: startTime,
        timestamp__hour__lt__offset: endTime,
        confidence__events__gte: formData.confidence,
        event__metadata__type__in: eventRule,
      };
    }

    switch (formData.label) {
      case 'motion':
      case 'camera-motion':
        params.confidence__gte = 0;
        break;

      case 'car':
        params.metadata__vehicle_type__in = formData.vehicleType;
        params.metadata__car_color__in = formData.carColors;
        params.metadata__car_color__confidence__gte = formData.colorConfidence;
        break;
      case 'person':
        if (formData.is_male === '>=50') {
          params.metadata__attributes__is_male__confidence__gte = 50;
        } else if (formData.is_male === '< 50') {
          params.metadata__attributes__is_male__confidence__lte = 50;
        }
        if (formData.has_hat === '>=50') {
          params.metadata__attributes__has_hat__confidence__gte = 50;
        } else if (formData.has_hat === '< 50') {
          params.metadata__attributes__has_hat__confidence__lte = 50;
        }
        if (formData.has_backpack === '>=50') {
          params.metadata__attributes__has_backpack__confidence__gte = 50;
        } else if (formData.has_backpack === '< 50') {
          params.metadata__attributes__has_backpack__confidence__lte = 50;
        }
        if (formData.has_bag === '>=50') {
          params.metadata__attributes__has_bag__confidence__gte = 50;
        } else if (formData.has_bag === '< 50') {
          params.metadata__attributes__has_bag__confidence__lte = 50;
        }
        if (formData.colorTop) {
          params.metadata__color_top__in = formData.colorTop;
        }

        if (formData.colorBottom) {
          params.metadata__color_bottom__in = formData.colorBottom;
        }
        break;
    }
    return params;
  }

  private _fetchFacets(): void {
    this.loadingFacets = true;
    this._camerasAPI
      .getAiList({
        pageSize: 1,
        camera_id__in: this.formModel.aiCameras,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        this._colors$.next(res.facetFields.carColors);
        this._vehicleTypes$.next(res.facetFields.vehicleTypes);
        this._detectionLabels$.next(res.facetFields.detectionLabels);
        this._eventLabels$.next(res.facetFields.eventLabels);
        this._eventRules$.next(res.facetFields.eventMetadataType);
        this._colorTop$.next(res.facetFields.colorTop);
        this._colorBottom$.next(res.facetFields.colorBottom);

        if (res.facetFields.eventLabels.length === 0) {
          this.formFields[1].fieldGroup[0].templateOptions.disabled = true;
          this.formFields[1].fieldGroup[0].templateOptions.description = t('map.tools.sentrySearchForm.noEventsAvailable');
        }

        if (res.facetFields.eventLabels.length > 0) {
          this.formFields[1].fieldGroup[0].templateOptions.disabled = false;
          this.formFields[1].fieldGroup[0].templateOptions.description = '';
        }

        this._cd.detectChanges();
        this.loadingFacets = false;
      }, (error) => {
        this.loadingFacets = false;
      });
  }

  private _fetchAiCameras(params: ISnListQueryParameters): Observable<ISnListResponse<SnCamera>> {
    return this._camerasAPI.getCameraList(params);
  }

  private _patchForm(data: any): void {
    this.formModel = data;
    if (!this.data.startDate) {
      const today = new Date();
      const yesterday = new Date();
      const tomorrow = new Date();
      yesterday.setDate(today.getDate() - 1);
      tomorrow.setDate(today.getDate() + 1);
      this.formModel = {
        ...this.formModel,
        startDate: yesterday,
        endDate: tomorrow,
      };
    }
  }

  private _patchEditForm(data: any): void {
    if (!data.rule.fields?.events_Label_In && !data.rule.fields?.confidence_Events_Gte) {
      this.formModel = {
        ...this.formModel,
        name: data.name,
        aiCameras: data.rule.fields.cameraId_In,
        eventDetection: 'detection',
        label: data.rule.fields?.label_In?.toString(),
        vehicleType: data.rule.fields?.metadata_VehicleType_In
          ? data.rule.fields?.metadata_VehicleType_In[0].replace('/', ' / ') ||
            data.rule.fields?.metadata_VehicleType_In[0]
          : null,
        carColors: data.rule.fields?.metadata_CarColor_In
          ? data.rule.fields?.metadata_CarColor_In[0]
          : null,
        confidence: data.rule.fields?.confidence_Gte,
        startDate: new Date(data.rule.fields?.timestamp_Gte * 1000),
        endDate: data.rule.fields?.timestamp_Lte
          ? new Date(data.rule.fields?.timestamp_Lte * 1000)
          : null,
        colorConfidence: data.rule.fields?.metadata_CarColor_Confidence_Gte,
        isActiveAlert: data.isActiveAlert,
        is_male: this._parseMetaField('IsMale', data.rule.fields),
        has_hat: this._parseMetaField('HasHat', data.rule.fields),
        has_backpack: this._parseMetaField('HasBackpack', data.rule.fields),
        has_bag: this._parseMetaField('HasBag', data.rule.fields),
        colorTop: data.rule.fields?.metadata_ColorTop_In,
        colorBottom: data.rule.fields?.metadata_ColorBottom_In,
        noEnd: !data.rule.fields?.timestamp_Lte,
        notificationCreateAlarm: data.notificationCreateAlarm,
        lockTime: data.lockTime,
      };
    } else {
      this.formModel = {
        ...this.formModel,
        name: data.name,
        aiCameras: data.rule.fields.cameraId_In,
        eventDetection: 'event',
        eventLabel: data.rule.fields?.events_Label_In,
        eventRule: data.rule.fields?.event_Metadata_Type_In,
        confidence: data.rule.fields?.confidence_Events_Gte,
        startDate: new Date(data.rule.fields?.timestamp_Gte * 1000),
        endDate: data.rule.fields?.timestamp_Lte
          ? new Date(data.rule.fields?.timestamp_Lte * 1000)
          : null,
        colorConfidence: data.rule.fields?.metadata_CarColor_Confidence_Gte,
        isActiveAlert: data.isActiveAlert,
        noEnd: !data.rule.fields?.timestamp_Lte,
        notificationCreateAlarm: data.notificationCreateAlarm,
        lockTime: data.lockTime,
      };
    }
    this._initForm();

    const ids = [];
    data.rule.notificationUsers.forEach((users) => {
      this._setNotification(users);
      ids.push(users.user);
    });
    this._usersAPI
      .getUserList({
        pageSize: 'all',
        id__in: ids.toString(),
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        this.selectedUsers = response.results;
      });
    this.form.value.notificationUsers = data.rule.notificationUsers;
    this.startTime = parseFloat(data.rule.fields.timestamp_Hour_Gte_Offset) * 60;
    this.endTime = parseFloat(data.rule.fields.timestamp_Hour_Lt_Offset) * 60;
    this.schedules = data.rule.schedules;
  }

  private _parseMetaField(field: string, data: any): any {
    const gte = data[`metadata_Attributes_${field}_Confidence_Gte`];
    const lte = data[`metadata_Attributes_${field}_Confidence_Lte`];
    if (gte) {
      return '>=50';
    }
    if (lte) {
      return '< 50';
    }
  }

  private _setForm(): void {
    this.formFields = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            key: 'name',
            type: 'input',
            className: 'fs-col-6',
            templateOptions: {
              appearance: 'outline',
              floatLabel: 'always',
              placeholder: t('map.tools.createSentryDialog.form.namePlaceholder'),
              label: t('common.name'),
              valueProp: 'name',
              labelProp: 'name',
              required: true,
            },
          },
          {
            key: 'aiCameras',
            type: 'http-select',
            className: 'fs-col-6',
            templateOptions: {
              appearance: 'outline',
              floatLabel: 'always',
              placeholder: t('map.tools.createSentryDialog.form.aiCamerasPlaceholder'),
              required: true,
              label: t('map.tools.createSentryDialog.form.aiCameras'),
              valueProp: 'id',
              labelProp: 'name',
              multiple: true,
              selectAllOption: t('common.selectAll'),
              clearable: true,
              fetchDataFn: () =>
                this._fetchAiCameras({
                  id__in: this.formModel.aiCameras.join(','),
                }),
            },
            hideExpression: () => this.isNew,
          },
        ],
      },
      {
        fieldGroup: [
          {
            key: 'eventDetection',
            type: 'radio',
            className: 'fs-col-12',
            defaultValue: 'detection',
            templateOptions: {
              label: t('map.tools.createSentryDialog.form.eventOrDetection'),
              appearance: 'outline',
              inline: true,
              options: [
                { label: t('map.tools.createSentryDialog.form.detection'), value: 'detection' },
                { label: t('map.tools.createSentryDialog.form.event'), value: 'event' },
              ],
            },
            hideExpression: () => this.isNew,
          },
          {
            key: 'label',
            type: 'select',
            className: 'fs-col-4',
            hide: false,
            templateOptions: {
              appearance: 'outline',
              options: this.detectionLabels$,
              floatLabel: 'always',
              placeholder: t('map.tools.createSentryDialog.form.detectionTypePlaceholder'),
              label: t('map.tools.createSentryDialog.form.detections'),
              valueProp: 'key',
              labelProp: 'key',
              emptyLabel: t('common.any'),
              emptyOption: true,
              clearable: true,
            },
            hideExpression: () => this.formModel.eventDetection === 'event',
          },
          {
            key: 'eventLabel',
            type: 'select',
            className: 'fs-col-4',
            hide: true,
            templateOptions: {
              appearance: 'outline',
              options: this.eventLabels$,
              floatLabel: 'always',
              placeholder: t('map.tools.createSentryDialog.form.eventTypePlaceholder'),
              label: t('map.tools.createSentryDialog.form.eventType'),
              valueProp: 'key',
              labelProp: 'key',
              emptyLabel: t('common.any'),
              emptyOption: true,
              clearable: true,
            },
            hideExpression: () => this.formModel.eventDetection === 'detection',
          },
          {
            key: 'eventRule',
            type: 'select',
            className: 'fs-col-4',
            hide: true,
            templateOptions: {
              appearance: 'outline',
              options: this.eventRules$,
              floatLabel: 'always',
              placeholder: t('map.tools.createSentryDialog.form.eventRulePlaceholder'),
              label: t('map.tools.createSentryDialog.form.eventRule'),
              valueProp: 'key',
              labelProp: 'key',
              emptyLabel: t('common.any'),
              emptyOption: true,
              clearable: true,
            },
            hideExpression: () => this.formModel.eventDetection === 'detection',
          },
        ],
      },
      {
        key: 'confidence',
        type: 'slider',
        defaultValue: 75,
        templateOptions: {
          label: t('common.confidence'),
          min: 0,
          max: 100,
          thumbLabel: true,
          hideFieldUnderline: true,
        },
        hideExpression: () => {
          return this.form.value.label === 'motion' || this.form.value.label === 'camera-motion';
        },
      },
      {
        fieldGroupClassName: 'fs-row',
        fieldGroup: [
          {
            key: 'is_male',
            type: 'select',
            className: 'fs-col-6',
            templateOptions: {
              appearance: 'outline',
              floatLabel: 'always',
              placeholder: t('map.tools.createSentryDialog.form.genderPlaceholder'),
              options: [
                { label: t('common.either'), value: '' },
                { label: t('common.male'), value: '>=50' },
                { label: t('common.female'), value: '< 50' },
              ],
              label: t('common.gender'),
              clearable: true,
            },
            hideExpression: () => {
              return this.form.value.label !== 'person';
            },
          },
          {
            key: 'has_hat',
            type: 'select',
            className: 'fs-col-6',
            templateOptions: {
              appearance: 'outline',
              floatLabel: 'always',
              placeholder: t('map.tools.createSentryDialog.form.hasHatPlaceholder'),
              options: [
                { label: t('common.either'), value: '' },
                { label: t('common.yes'), value: '>=50' },
                { label: t('common.no'), value: '< 50' },
              ],
              clearable: true,
            },
            hideExpression: () => {
              return this.form.value.label !== 'person';
            },
          },
        ],
      },
      {
        fieldGroupClassName: 'fs-row',
        fieldGroup: [
          {
            key: 'colorTop',
            type: 'select',
            className: 'fs-col-6',
            templateOptions: {
              appearance: 'outline',
              options: this.colorTop$,
              floatLabel: 'always',
              placeholder: t('common.selectTopColor'),
              label: t('common.topColor'),
              valueProp: 'key',
              labelProp: 'key',
              emptyLabel: t('common.any'),
              emptyOption: true,
              clearable: true,
            },
            hideExpression: () => {
              return this.form.value.label !== 'person';
            },
          },
          {
            key: 'colorBottom',
            type: 'select',
            className: 'fs-col-6',
            templateOptions: {
              appearance: 'outline',
              options: this.colorBottom$,
              floatLabel: 'always',
              placeholder: t('common.selectBottomColor'),
              label: t('common.bottomColor'),
              valueProp: 'key',
              labelProp: 'key',
              emptyLabel: t('common.any'),
              emptyOption: true,
              clearable: true,
            },
            hideExpression: () => {
              return this.form.value.label !== 'person';
            },
          },
        ],
      },
      {
        fieldGroupClassName: 'fs-row',
        fieldGroup: [
          {
            key: 'vehicleType',
            type: 'select',
            className: 'fs-col-6',
            templateOptions: {
              appearance: 'outline',
              options: this.vehicleTypes$,
              floatLabel: 'always',
              placeholder: t('map.findVehicle.selectVehicleType'),
              label: t('common.vehicleType'),
              valueProp: 'key',
              labelProp: 'key',
              clearable: true,
            },
            hideExpression: () => {
              return this.form.value.label !== 'car';
            },
          },
          {
            key: 'carColors',
            type: 'select',
            className: 'fs-col-6',
            templateOptions: {
              appearance: 'outline',
              floatLabel: 'always',
              placeholder: t('common.selectColor'),
              infinityScroll: true,
              options: this.colors$,
              label: t('common.color'),
              labelProp: 'key',
              valueProp: 'key',
              clearable: true,
            },
            hideExpression: () => {
              return this.form.value.label !== 'car';
            },
          },
        ],
      },
      {
        key: 'colorConfidence',
        type: 'slider',
        defaultValue: 75,
        templateOptions: {
          label: t('map.tools.createSentryDialog.form.carColorConfidence'),
          min: 0,
          max: 100,
          thumbLabel: true,
          hideFieldUnderline: true,
        },
        hideExpression: () => {
          return this.form.value.label !== 'car' || !this.form.value.carColors;
        },
      },
      {
        fieldGroupClassName: 'fs-row',
        fieldGroup: [
          {
            key: 'has_bag',
            type: 'select',
            className: 'fs-col-6',
            templateOptions: {
              appearance: 'outline',
              floatLabel: 'always',
              placeholder: t('map.tools.createSentryDialog.form.hasBagPlaceholder'),
              options: [
                { label: t('common.either'), value: '' },
                { label: t('common.yes'), value: '>=50' },
                { label: t('common.no'), value: '< 50' },
              ],
              clearable: true,
            },
            hideExpression: () => {
              return this.form.value.label !== 'person';
            },
          },
          {
            key: 'has_backpack',
            type: 'select',
            className: 'fs-col-6',
            templateOptions: {
              appearance: 'outline',
              floatLabel: 'always',
              placeholder: t('map.tools.createSentryDialog.form.hasBackpackPlaceholder'),
              options: [
                { label: t('common.either'), value: '' },
                { label: t('common.yes'), value: '>=50' },
                { label: t('common.no'), value: '< 50' },
              ],
              clearable: true,
            },
            hideExpression: () => {
              return this.form.value.label !== 'person';
            },
          },
        ],
      },
      {
        key: 'startDate',
        type: 'datetimepicker',
        templateOptions: {
          label: t('common.startDate'),
          appearance: 'fill',
          required: true,
          maxDate: () => this.formModel.endDate,
        },
      },
      {
        key: 'noEnd',
        type: 'checkbox',
        templateOptions: {
          label: t('common.forever'),
          appearance: 'fill',
          required: false,
        },
      },
      {
        key: 'endDate',
        type: 'datetimepicker',
        templateOptions: {
          label: t('common.endDate'),
          appearance: 'fill',
          required: true,
        },
        hideExpression: () => {
          return this.formModel.noEnd;
        },
      },
      {
        key: 'notificationCreateAlarm',
        type: 'checkbox',
        defaultValue: false,
        templateOptions: {
          label: t('map.tools.createSentryDialog.form.triggerAlert'),
        },
      },
      {
        key: 'lockTime',
        type: 'input',
        templateOptions: {
          type: 'number',
          label: t('map.tools.createSentryDialog.form.timeoutPeriod'),
          placeholder: t('map.tools.createSentryDialog.form.timeoutPlaceholder'),
          min: 60,
          max: 3600,
        },
        validation: {
          messages: {
            min: t('map.tools.createSentryDialog.form.validation.timeoutMin'),
            max: t('map.tools.createSentryDialog.form.validation.timeoutMax'),
          },
        },
        hideExpression: () => !this.formModel.notificationCreateAlarm,
      },
    ];
  }
}
